import { z } from 'zod';

import { paginationSchema } from '~/shared/data-contract/pagination';
import {
    automationRecommendationStateFieldSchema,
    profileTaskAssessmentStateTransportSchema,
    profileTaskAssessmentTransportSchema,
    profileTaskClassificationTransportSchema,
    profileTaskEstimatedEffortTransportSchema,
    profileTaskGenerationStateTransportSchema,
    profileTaskRequiredSkillTransportSchema,
    profileTaskWorkflowTransportSchema,
} from '~/shared/data-contract/profile-task';

const opaqueIdSchema = z.string().trim().min(1);
const nonEmptyTextSchema = z.string().trim().min(1);

export const TASK_MANAGEMENT_DEFAULT_PAGE_SIZE = 50;

/** Another task named by id and title only; everything else is read through the task endpoints. */
export const taskManagementTaskRefSchema = z.object({
    id: opaqueIdSchema,
    title: nonEmptyTextSchema,
});

export type TaskManagementTaskRef = z.infer<typeof taskManagementTaskRefSchema>;

/**
 * `parents` are the tasks this one derives from, `children` the tasks derived from it, and
 * `related` are undirected links. Each list may be empty.
 */
export const taskManagementTaskSchema = z.object({
    children: taskManagementTaskRefSchema.array().default([]),
    classification: profileTaskClassificationTransportSchema.optional(),
    estimatedEffort: profileTaskEstimatedEffortTransportSchema.optional(),
    id: opaqueIdSchema,
    parents: taskManagementTaskRefSchema.array().default([]),
    related: taskManagementTaskRefSchema.array().default([]),
    taskVersionId: opaqueIdSchema,
    title: nonEmptyTextSchema,
    version: z.number().int().positive(),
});

export type TaskManagementTask = z.infer<typeof taskManagementTaskSchema>;

/** The single-task read: the list shape plus the generated content. */
export const taskManagementTaskDetailSchema = taskManagementTaskSchema.extend({
    automationRecommendationState: automationRecommendationStateFieldSchema,
    // Display-only, so a malformed assessment hides itself rather than failing the task read.
    assessment: profileTaskAssessmentTransportSchema.optional().catch(undefined),
    assessmentState: profileTaskAssessmentStateTransportSchema.optional().catch(undefined),
    description: z.string().optional(),
    generationState: profileTaskGenerationStateTransportSchema.default('finished'),
    requiredSkills: profileTaskRequiredSkillTransportSchema.array().default([]),
    workflow: profileTaskWorkflowTransportSchema.optional(),
});

export type TaskManagementTaskDetail = z.infer<typeof taskManagementTaskDetailSchema>;

const percentageSchema = z.number().min(0).max(100);
// Display-only labels: an unknown value hides itself instead of failing the recommendation.
const complexityLevelSchema = z.enum(['low', 'medium', 'high']).optional().catch(undefined);
const autonomyLevelSchema = z.number().int().min(1).max(5).optional().catch(undefined);
const workflowIndexSchema = z.number().int().nonnegative().optional();

const workTaskWorkflowAutomationSchema = z.object({
    automatablePercentage: percentageSchema.optional(),
    workflowIndex: workflowIndexSchema,
});

const automationInstructionStepSchema = z.object({
    actor: z.enum(['human', 'ai', 'system']).optional().catch(undefined),
    description: z.string().optional(),
    phase: z.enum(['setup', 'run']).optional().catch(undefined),
    title: z.string().optional(),
});

export type WorkTaskAutomationInstructionStep = z.infer<typeof automationInstructionStepSchema>;

const automationTriggerKindSchema = z.enum(['event', 'schedule', 'manual']);

export type WorkTaskAutomationTriggerKind = z.infer<typeof automationTriggerKindSchema>;

const automationInstructionsSchema = z.object({
    brief: z.string().optional(),
    constraints: z.string().array().default([]),
    steps: automationInstructionStepSchema.array().default([]),
    trigger: z
        .object({
            description: z.string().optional(),
            kind: automationTriggerKindSchema.optional().catch(undefined),
        })
        .optional(),
});

export type WorkTaskAutomationInstructions = z.infer<typeof automationInstructionsSchema>;

export const workTaskAutomationSchema = z.object({
    automatablePercentage: percentageSchema.optional(),
    automationId: opaqueIdSchema,
    automationReasoning: z.string().optional(),
    autonomyLevel: autonomyLevelSchema,
    businessFunction: z.string().optional(),
    catalogueStatus: z.string().optional(),
    complexityLevel: complexityLevelSchema,
    description: z.string().optional(),
    // Display-only build plan, so a malformed one hides itself instead of failing the recommendation.
    instructions: automationInstructionsSchema.optional().catch(undefined),
    mergedIntoId: opaqueIdSchema.nullish(),
    products: z.object({ name: nonEmptyTextSchema, productId: opaqueIdSchema }).array().default([]),
    score: z.number().min(0).max(1),
    scoreReasoning: z.string().optional(),
    shortDefinition: z.string().optional(),
    title: z.string().optional(),
    workflows: workTaskWorkflowAutomationSchema
        .extend({
            autonomyLevel: autonomyLevelSchema,
            complexityLevel: complexityLevelSchema,
            reasoning: z.string().optional(),
        })
        .array()
        .default([]),
});

export type WorkTaskAutomation = z.infer<typeof workTaskAutomationSchema>;

/** `GET /work-tasks/{taskId}/automation-recommendation`; percentages and automations only once succeeded. */
export const workTaskAutomationRecommendationSchema = z.object({
    automatablePercentage: percentageSchema.optional(),
    automations: workTaskAutomationSchema.array().default([]),
    currentAutomation: workTaskWorkflowAutomationSchema.array().default([]),
    status: z.enum(['pending', 'succeeded', 'failed', 'unavailable']).catch('unavailable'),
    taskId: opaqueIdSchema,
});

export type WorkTaskAutomationRecommendation = z.infer<typeof workTaskAutomationRecommendationSchema>;

export const taskManagementTasksResponseSchema = z.object({
    data: taskManagementTaskSchema.array(),
    pagination: paginationSchema,
});

export type TaskManagementTasksResponse = z.infer<typeof taskManagementTasksResponseSchema>;

export const taskManagementClusterSchema = z.union([
    z.object({
        id: opaqueIdSchema,
        name: nonEmptyTextSchema,
        taskIds: opaqueIdSchema.array(),
    }),
    z.object({
        id: z.null(),
        name: z.null(),
        taskIds: opaqueIdSchema.array(),
    }),
]);

export const taskManagementClustersResponseSchema = taskManagementClusterSchema.array();

export type TaskManagementCluster = z.infer<typeof taskManagementClusterSchema>;
export type TaskManagementClustersResponse = z.infer<typeof taskManagementClustersResponseSchema>;

const taskManagementPagedSchema = z.object({
    page: z.number().int().nonnegative().default(0),
    size: z.number().int().positive().default(TASK_MANAGEMENT_DEFAULT_PAGE_SIZE),
});

export const taskManagementSearchRequestSchema = z.object({
    architectureIds: opaqueIdSchema.array().optional(),
    paged: taskManagementPagedSchema.default({ page: 0, size: TASK_MANAGEMENT_DEFAULT_PAGE_SIZE }),
    searchTerm: z.string().optional(),
});

export type TaskManagementSearchRequest = z.infer<typeof taskManagementSearchRequestSchema>;
export type TaskManagementSearchRequestInput = z.input<typeof taskManagementSearchRequestSchema>;

export const taskManagementClusterSearchRequestSchema = z.object({
    clusterId: opaqueIdSchema.nullable(),
    paged: taskManagementPagedSchema.default({ page: 0, size: TASK_MANAGEMENT_DEFAULT_PAGE_SIZE }),
    searchTerm: z.string().optional(),
});

export type TaskManagementClusterSearchRequest = z.infer<typeof taskManagementClusterSearchRequestSchema>;
